import { useState } from 'react'
import { Car, Loader2, Plus, Warehouse } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { equipment as equipmentApi } from '@/utils/api'
import { useEquipmentAction, useEquipmentPlaces } from '@/hooks/useEquipment'
import type { EquipmentPlace } from '@/types/equipment'

/** Lager und Firmenfahrzeuge als Geräte-Standorte pflegen. */
export default function OrteVerwaltung() {
  const { data: places, isLoading } = useEquipmentPlaces(true)
  const [type, setType] = useState<'lager' | 'fahrzeug'>('fahrzeug')
  const [name, setName] = useState('')
  const [plate, setPlate] = useState('')

  const create = useEquipmentAction(
    () => equipmentApi.createPlace({ type, name, licensePlate: plate || null }),
    'Ort angelegt',
  )
  const toggle = useEquipmentAction(
    (p: EquipmentPlace) => equipmentApi.updatePlace(p.id, { isActive: !p.is_active }),
    'Gespeichert',
  )
  const rename = useEquipmentAction(
    ({ p, patch }: { p: EquipmentPlace; patch: Record<string, string | null> }) => equipmentApi.updatePlace(p.id, patch),
    'Gespeichert',
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    create.mutate(undefined, { onSuccess: () => { setName(''); setPlate('') } })
  }

  const groups: ['lager' | 'fahrzeug', string, typeof Warehouse][] = [['lager', 'Lager', Warehouse], ['fahrzeug', 'Fahrzeuge', Car]]

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-6">
        {isLoading ? <Skeleton className="h-40" /> : groups.map(([t, title, Icon]) => (
          <div key={t} className="overflow-hidden rounded-lg border bg-white">
            <p className="flex items-center gap-2 border-b bg-gray-50 px-3 py-2 text-sm font-semibold"><Icon className="h-4 w-4" /> {title}</p>
            {(places ?? []).filter((p) => p.type === t).length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400">Noch keine {title.toLowerCase()} angelegt.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {(places ?? []).filter((p) => p.type === t).map((p) => (
                    <tr key={p.id} className={p.is_active ? '' : 'text-gray-400'}>
                      <td className="border-t px-3 py-2">
                        <input
                          defaultValue={p.name}
                          onBlur={(e) => e.target.value.trim() && e.target.value !== p.name && rename.mutate({ p, patch: { name: e.target.value.trim() } })}
                          className="w-full rounded border-transparent bg-transparent px-1 hover:border-gray-200"
                        />
                      </td>
                      {t === 'fahrzeug' && (
                        <td className="border-t px-3 py-2">
                          <input
                            defaultValue={p.license_plate ?? ''}
                            placeholder="Kennzeichen"
                            onBlur={(e) => e.target.value !== (p.license_plate ?? '') && rename.mutate({ p, patch: { licensePlate: e.target.value.trim() || null } })}
                            className="w-32 rounded border-transparent bg-transparent px-1 font-mono uppercase hover:border-gray-200"
                          />
                        </td>
                      )}
                      <td className="border-t px-3 py-2 text-right text-gray-500">{p.equipment_count ?? 0} Geräte</td>
                      <td className="border-t px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => toggle.mutate(p)} disabled={toggle.isPending}>
                          {p.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="h-fit space-y-3 rounded-lg border bg-white p-4">
        <p className="font-semibold">Neuer Ort</p>
        <div className="flex gap-2">
          {groups.map(([t, title, Icon]) => (
            <Button key={t} type="button" size="sm" variant={type === t ? 'default' : 'outline'} onClick={() => setType(t)}>
              <Icon className="h-4 w-4" /> {title === 'Fahrzeuge' ? 'Fahrzeug' : title}
            </Button>
          ))}
        </div>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={type === 'fahrzeug' ? 'z.B. Sprinter Team Nord' : 'z.B. Container Baustelle X'} />
        {type === 'fahrzeug' && <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="Kennzeichen, z.B. B-CP 123" />}
        <Button type="submit" disabled={!name.trim() || create.isPending} className="w-full">
          {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Anlegen
        </Button>
      </form>
    </div>
  )
}
